import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { getAnalyticsData } from '@/lib/admin/analytics';
import type { AnalyticsData } from '@/lib/admin/analytics';
import { DateRangeSelector, type DateRangePreset, type DateRange } from '@/components/admin/analytics/DateRangeSelector';
import { KPICard } from '@/components/admin/analytics/KPICard';
import { StatusDistributionChart } from '@/components/admin/analytics/StatusDistributionChart';
import { CategoryPerformanceChart } from '@/components/admin/analytics/CategoryPerformanceChart';
import { PriceAnalyticsChart } from '@/components/admin/analytics/PriceAnalyticsChart';
import { UnitInterestRanking } from '@/components/admin/analytics/UnitInterestRanking';
import { BlockFloorDistribution } from '@/components/admin/analytics/BlockFloorDistribution';
import { ConversionFunnel } from '@/components/admin/analytics/ConversionFunnel';
import { RevenuePotentialChart } from '@/components/admin/analytics/RevenuePotentialChart';
import { RecommendationsPanel } from '@/components/admin/analytics/RecommendationsPanel';
import { Package, DollarSign, TrendingUp, Users } from 'lucide-react';

const AdminAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [datePreset, setDatePreset] = useState<DateRangePreset>('all');

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: dataError } = await getAnalyticsData(
        dateRange.startDate,
        dateRange.endDate
      );
      if (dataError) {
        setError(dataError);
      } else {
        setAnalyticsData(data);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load analytics data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            <p className="font-semibold">Error loading analytics</p>
            <p className="text-sm mt-2">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analyticsData) {
    return null;
  }

  const { inventory, revenue, categoryDistribution, blockDistribution, floorDistribution, unitInterest, conversion } = analyticsData;

  return (
    <div className="space-y-6">
      {/* Header with Date Range Selector */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Analytics Dashboard</CardTitle>
              <CardDescription>
                Comprehensive insights into unit performance and sales metrics
              </CardDescription>
            </div>
            <DateRangeSelector
              value={dateRange}
              onChange={setDateRange}
              preset={datePreset}
              onPresetChange={setDatePreset}
            />
          </div>
        </CardHeader>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Units"
          value={inventory.total}
          description={`${inventory.available} available, ${inventory.reserved} reserved, ${inventory.sold} sold`}
          icon={<Package className="h-4 w-4" />}
        />
        <KPICard
          title="Portfolio Value"
          value={revenue.totalPortfolioValue}
          description={`${formatCurrency(revenue.availableValue)} available`}
          icon={<DollarSign className="h-4 w-4" />}
        />
        <KPICard
          title="Sold Value"
          value={revenue.soldValue}
          description={`${inventory.sold} units sold`}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <KPICard
          title="Conversion Rate"
          value={`${conversion.overallConversion.toFixed(1)}%`}
          description={`${inventory.sold} of ${inventory.total} units sold`}
          icon={<Users className="h-4 w-4" />}
        />
      </div>

      {/* Main Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StatusDistributionChart data={inventory} />
        <ConversionFunnel inventory={inventory} conversion={conversion} />
      </div>

      {/* Category and Price Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CategoryPerformanceChart data={categoryDistribution} />
        <PriceAnalyticsChart revenue={revenue} categoryData={categoryDistribution} />
      </div>

      {/* Revenue Potential */}
      <RevenuePotentialChart revenue={revenue} />

      {/* Block and Floor Distribution */}
      <BlockFloorDistribution
        blockData={blockDistribution}
        floorData={floorDistribution}
      />

      {/* Unit Interest and Recommendations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <UnitInterestRanking data={unitInterest} />
        <RecommendationsPanel
          units={analyticsData.units}
          categoryData={categoryDistribution}
          revenue={revenue}
          unitInterest={unitInterest}
        />
      </div>
    </div>
  );
};

export default AdminAnalytics;

